import { Check, ChevronDown, ChevronUp, DotsVertical, Edit01, PhoneCall01, Trash01, User01 } from '@untitled-ui/icons-react/build/cjs';
import React, { useEffect, useRef, useState } from 'react';
import BadgeCmp from 'src/components/BadgeCmp';
import OutsideClickHandler from 'src/components/OutsideClickHandler';
import TooltipCmp from 'src/components/TooltipCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import { usePhone as usePhoneCommon } from 'src/utils/CommonFunctions';

interface AddressGridViewProps {
    isLoading: boolean;
    addresses: any[];
    getAddressTypes: (address: any) => any[];
    getAdditionalServices: (address: any) => any[];
    onEdit: (id: string) => void;
    onDelete: (address: any) => void;
    t: (key: string) => string;
}

const AddressCard = ({ address, getAddressTypes, getAdditionalServices, onEdit, onDelete, t }: any) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [openUpward, setOpenUpward] = useState(false);
    const triggerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isExpanded) return;
        const handleScroll = () => setIsExpanded(false);
        window.addEventListener('scroll', handleScroll, true);

        return () => window.removeEventListener('scroll', handleScroll, true);
    }, [isExpanded]);
    const { getFormatPhoneWithPlus } = usePhoneCommon();
    const companyNameRef = useRef<HTMLParagraphElement>(null);
    const fullAddressRef = useRef<HTMLParagraphElement>(null);
    const contactNameRef = useRef<HTMLParagraphElement>(null);
    const contactNumberRef = useRef<HTMLParagraphElement>(null);

    const addressTypes = getAddressTypes(address);
    const services = getAdditionalServices(address);

    return (
        <div className={`rounded-lg border border-utilityGray200 shadow-xs bg-white flex flex-col relative ${services.length > 0 && isExpanded && !openUpward ? 'rounded-b-none' : 'rounded-b-lg'}`}>
            {/* Top section */}
            <div className="flex flex-col py-4 px-3">
                <div className="flex flex-col mb-2.5">
                    {/* Name + badges + menu */}
                    <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="flex items-center gap-x-2 gap-y-1 flex-1 min-w-0">
                            <TruncateTextCmp message={address?.companyName || '-'} refName={companyNameRef}>
                                <p ref={companyNameRef} className="text-grayLight900 text-sm font-medium truncate">
                                    {address?.companyName || '-'}
                                </p>
                            </TruncateTextCmp>
                            <div className="flex items-center gap-x-1">
                                {addressTypes.map((type: any, i: number) => (
                                    <BadgeCmp key={i} style="modern" type={type.style}>
                                        {type.label}
                                    </BadgeCmp>
                                ))}
                            </div>
                        </div>

                        {/* Three-dot menu */}
                        <OutsideClickHandler onOutsideClick={() => setIsMenuOpen(false)} containerClassName="relative flex-none">
                            <span className="rounded-md text-gray400 cursor-pointer" onClick={() => setIsMenuOpen((prev) => !prev)}>
                                <DotsVertical className="w-4 h-4" />
                            </span>
                            <ul
                                className={`shadow text-xs rounded-lg break-words cursor-pointer empty:hidden bg-white border border-utilityGray200 p-1 my-1.5 z-[1] w-[120px] right-0 absolute leading-[18px] ${
                                    isMenuOpen ? '' : 'hidden'
                                }`}
                            >
                                <li
                                    className="text-xs leading-[1.5] font-normal text-grayLight900 rounded-md bg-transparent hover:bg-utilityGray100 p-1 transition-all duration-[0.3s] cursor-pointer flex items-center gap-x-2"
                                    onClick={() => {
                                        setIsMenuOpen(false);
                                        onEdit(address?.id);
                                    }}
                                >
                                    <Edit01 className="w-3.5 h-3.5 text-grayLight900 flex-none" />
                                    <p className="flex-1">{t('Common.Edit')}</p>
                                </li>
                                <li
                                    className="text-xs leading-[1.5] font-normal text-fgErrorPrimary rounded-md bg-transparent hover:bg-utilityGray100 p-1 transition-all duration-[0.3s] cursor-pointer flex items-center gap-x-2"
                                    onClick={() => {
                                        setIsMenuOpen(false);
                                        onDelete(address);
                                    }}
                                >
                                    <Trash01 className="w-3.5 h-3.5 text-fgErrorPrimary flex-none" />
                                    <p className="flex-1">{t('Common.Delete')}</p>
                                </li>
                            </ul>
                        </OutsideClickHandler>
                    </div>

                    {/* Address */}
                    <TruncateTextCmp message={address?.fullAddress || '-'} refName={fullAddressRef}>
                        <p ref={fullAddressRef} className="text-grayLight600 text-[12px] leading-[18px] font-normal truncate">
                            {address?.fullAddress || '-'}
                        </p>
                    </TruncateTextCmp>
                </div>

                {/* Contact row */}
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-x-1.5 min-w-0">
                        <User01 className="w-4 h-4 text-gray400 flex-none" />
                        <TruncateTextCmp message={address?.contactName || '-'} refName={contactNameRef}>
                            <p ref={contactNameRef} className="text-grayLight600 text-xs leading-[18px] font-medium truncate">
                                {address?.contactName || '-'}
                            </p>
                        </TruncateTextCmp>
                    </div>
                    <div className="flex items-center gap-x-1.5 flex-none min-w-[132px]">
                        <PhoneCall01 className="w-4 h-4 text-gray400 flex-none" />
                        <TruncateTextCmp message={address?.contactNumber ? getFormatPhoneWithPlus(address.contactNumber) : '-'} refName={contactNumberRef}>
                            <p ref={contactNumberRef} className="text-grayLight600 text-[12px] font-medium leading-[18px] text-nowrap">
                                {address?.contactNumber ? getFormatPhoneWithPlus(address.contactNumber) : '-'}
                            </p>
                        </TruncateTextCmp>
                    </div>
                </div>
            </div>

            {/* Additional Services row */}
            <OutsideClickHandler onOutsideClick={() => setIsExpanded(false)} containerClassName="relative">
                <div
                    ref={triggerRef}
                    className={`px-3 py-2 border-t border-utilityGray200 flex items-center justify-between gap-2 bg-gray50 rounded-b-lg ${services.length > 0 ? 'cursor-pointer' : ''} ${
                        services.length > 0 && isExpanded && !openUpward ? 'rounded-b-none' : 'rounded-b-lg'
                    }`}
                    onClick={() => {
                        if (!services.length) return;

                        if (triggerRef.current) {
                            const rect = triggerRef.current.getBoundingClientRect();
                            setOpenUpward(window.innerHeight - rect.bottom < 160);
                        }

                        setIsExpanded((prev) => !prev);
                    }}
                >
                    <div className="flex items-center gap-x-1.5">
                        <p className="text-gray500 leading-[18px] text-[12px] font-medium">{t('AddressBook.Additional_Services')}</p>
                        {services.length > 0 && (
                            <BadgeCmp style="modern" type="gray" isHidePillDot>
                                {services.length}
                            </BadgeCmp>
                        )}
                    </div>
                    {services.length > 0 ? (
                        <TooltipCmp message={isExpanded ? t('Common.Collapse') : t('Common.Expand')} position="top">
                            {isExpanded ? <ChevronUp className="w-4 h-4 text-gray400 flex-none" /> : <ChevronDown className="w-4 h-4 text-gray400 flex-none" />}
                        </TooltipCmp>
                    ) : null}
                </div>

                {/* Expandable services — absolute so it overlays without shifting layout */}
                {services.length > 0 && isExpanded && (
                    <div
                        className={`absolute left-0 right-0 z-10 border border-utilityGray200 bg-white px-2 py-2 -m-px  ${
                            openUpward ? 'bottom-[104%] rounded-t-xl border-b-0 shadow-[0_0px_16px_-4px_rgba(16,24,40,0.12)]' : 'rounded-b-xl shadow-[0_10px_16px_-4px_rgba(16,24,40,0.12)]'
                        }`}
                    >
                        <div className="flex flex-wrap gap-x-4 gap-y-2 border border-utilityGray200 rounded-md p-3">
                            {services.map((service: any) => (
                                <div key={service.id} className="flex items-center gap-x-1.5">
                                    <Check className="w-4 h-4 min-w-4 text-primary flex-none" />
                                    <p className="text-textSecondary text-xs font-medium leading-[18px] truncate">{service.label}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </OutsideClickHandler>
        </div>
    );
};

const AddressGridView: React.FC<AddressGridViewProps> = ({ isLoading, addresses, getAddressTypes, getAdditionalServices, onEdit, onDelete, t }) => {
    if (isLoading) {
        return (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 p-3">
                {Array.from({ length: 9 }).map((_, index) => (
                    <div key={index} className="rounded-lg border border-utilityGray200 shadow-xs bg-white flex flex-col">
                        {/* Top section */}
                        <div className="flex flex-col py-4 px-3">
                            <div className="flex flex-col mb-2.5">
                                {/* Name + badge + menu icon */}
                                <div className="flex items-start justify-between gap-2 mb-1">
                                    <div className="flex items-center gap-x-2 flex-1 min-w-0">
                                        <p className="custom-loading text-sm font-medium truncate">Lorem ipsum dolor</p>
                                        <span className="custom-loading rounded-full text-xs flex-none">Shipper</span>
                                    </div>
                                    <span className="custom-loading rounded-md w-4 h-4 flex-none" />
                                </div>
                                {/* Address */}
                                <p className="custom-loading text-[12px] leading-[18px]">123 Main Street, New York, NY</p>
                            </div>
                            {/* Contact row */}
                            <div className="flex items-center justify-between gap-2">
                                <p className="custom-loading text-xs">Lorem ipsum dolor</p>
                                <p className="custom-loading text-xs">+1 (555) 000-0000</p>
                            </div>
                        </div>
                        {/* Additional Services row */}
                        <div className={`px-3 py-2 border-t border-utilityGray200 flex items-center gap-x-1.5 bg-gray50 rounded-b-lg`}>
                            <p className="custom-loading text-[12px]">Additional Services</p>
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 p-3">
            {addresses.map((address: any) => (
                <AddressCard key={address.id} address={address} getAddressTypes={getAddressTypes} getAdditionalServices={getAdditionalServices} onEdit={onEdit} onDelete={onDelete} t={t} />
            ))}
        </div>
    );
};

export default AddressGridView;
